import { type ApolloQueryResult } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type Column,
  type ColumnRenderer,
  type ExplorerDataProvider,
  filterToPostGraphileFilter,
  FilterTypes,
  ImgElement,
  NavigationExplorer,
  type SortData,
  sortToPostGraphileOrderBy,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  CustomizableServicesDocument,
  type CustomizableServicesQuery,
  type CustomizableServicesQueryVariables,
  ResourceStatus,
  type ServiceDefinitionFilter,
  ServiceDefinitionsOrderBy,
} from '../../../../generated/hosting-service';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { getServiceDeploymentStatusIconLabel } from '../common/utils';
import { getEnumLabel } from '../CustomizableServiceDeployment/CustomizableServiceDeployment.filters';
import classes from './CustomizableServices.module.scss';

export type CustomizableService = NonNullable<
  NonNullable<
    NonNullable<CustomizableServicesQuery['filtered']>['nodes']
  >[number]
>;

const filterOptionsForResourceStatus: (keyof typeof ResourceStatus)[] = [
  'Ready',
  'DeleteCompleted',
  'CreatePending',
  'DeletePending',
  'CreateError',
  'DeleteError',
  'ScaledDown',
];

const deploymentStatusRenderer: ColumnRenderer<CustomizableService> = (
  _value,
  data,
): ReactNode => {
  const { icon, label } = getServiceDeploymentStatusIconLabel(
    data.activeDeploymentStatus as ResourceStatus,
  );

  return (
    <div className={classes.status_container}>
      <ImgElement
        src={icon}
        style={{ width: '20px', height: '20px' }}
        decorative={true}
      />
      <div className={classes.status}>{label}</div>
    </div>
  );
};

const OwnedByAxinomStatusRenderer: ColumnRenderer<CustomizableService> = (
  value,
  _data,
): ReactNode => {
  return (
    <div className={classes.status_container}>
      <div className={classes.status}>{value ? 'Yes' : 'No'}</div>
    </div>
  );
};

const explorerColumns: Column<CustomizableService>[] = [
  { label: 'Name', propertyName: 'name', size: '1fr', sortable: true },
  {
    label: 'Service ID',
    propertyName: 'serviceId',
    size: '1fr',
    sortable: false,
  },
  {
    label: 'Owned By Axinom',
    propertyName: 'ownedByAxinom',
    size: '1fr',
    sortable: false,
    render: OwnedByAxinomStatusRenderer,
  },
  {
    label: 'Status',
    propertyName: 'activeDeploymentStatus',
    size: '1fr',
    sortable: true,
    render: deploymentStatusRenderer,
  },
];

export const CustomizableServices: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
  }>();

  const isStatusSort = (
    sorting: SortData<CustomizableService> | undefined,
  ): boolean => sorting?.column === 'activeDeploymentStatus';

  const sortByStatus = (
    data: CustomizableService[],
    direction: 'asc' | 'desc',
  ): CustomizableService[] => {
    return [...data].sort((a, b) => {
      const left = getEnumLabel(a.activeDeploymentStatus ?? '').toLowerCase();
      const right = getEnumLabel(b.activeDeploymentStatus ?? '').toLowerCase();
      const comparison = left.localeCompare(right);

      return direction === 'asc' ? comparison : -comparison;
    });
  };

  const dataProvider: ExplorerDataProvider<CustomizableService> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );

      // Extract status filter – this should NOT go to backend
      const statusFilter = filters.activeDeploymentStatus;
      const { activeDeploymentStatus, ...backendFilters } = filters;

      const gqlFilter = filterToPostGraphileFilter<ServiceDefinitionFilter>(
        backendFilters,
        {
          serviceId: 'includesInsensitive',
          name: 'includesInsensitive',
          ownedByAxinom: 'equalTo',
        },
      );

      const fetchPage = (
        after?: unknown,
      ): Promise<ApolloQueryResult<CustomizableServicesQuery>> =>
        envApolloClient.query<
          CustomizableServicesQuery,
          CustomizableServicesQueryVariables
        >({
          query: CustomizableServicesDocument,
          variables: {
            filter: gqlFilter,
            orderBy: sortToPostGraphileOrderBy(
              sorting,
              ServiceDefinitionsOrderBy,
            ),
            after: typeof after === 'string' ? after : undefined,
          },
          fetchPolicy: 'no-cache',
        });

      // When sorting client-side by status, we must fetch ALL pages first so
      // that the sort operates over the complete dataset, not just the first page.
      let allNodes: CustomizableService[] = [];
      let nonFilteredTotal = 0;

      if (isStatusSort(sorting)) {
        let cursor: string | undefined = undefined;
        let hasMore = true;

        while (hasMore) {
          const page = await fetchPage(cursor);
          allNodes = allNodes.concat(page.data.filtered?.nodes ?? []);
          nonFilteredTotal = page.data.nonFiltered?.totalCount ?? 0;
          hasMore = page.data.filtered?.pageInfo.hasNextPage ?? false;
          cursor = page.data.filtered?.pageInfo.endCursor ?? undefined;
        }
      } else {
        const result = await fetchPage(pagingInformation);
        allNodes = result.data.filtered?.nodes ?? [];
        nonFilteredTotal = result.data.nonFiltered?.totalCount ?? 0;

        const patchedData = allNodes.map((item) => {
          if (
            item.ownedByAxinom &&
            item.activeDeploymentStatus === ResourceStatus.Ready &&
            item.evaluationPodExpirationDate === null
          ) {
            item.activeDeploymentStatus = ResourceStatus.ScaledDown;
          }

          return item;
        });

        const clientFiltered = statusFilter
          ? patchedData.filter(
              (item) => item.activeDeploymentStatus === statusFilter,
            )
          : patchedData;

        return {
          data: clientFiltered,
          totalCount: nonFilteredTotal,
          filteredCount: result.data.filtered?.totalCount ?? 0,
          hasMoreData: result.data.filtered?.pageInfo.hasNextPage ?? false,
          pagingInformation: result.data.filtered?.pageInfo.endCursor,
        };
      }

      // Status sort path – patch and filter the full dataset, then sort
      const patchedData = allNodes.map((item) => {
        if (
          item.ownedByAxinom &&
          item.activeDeploymentStatus === ResourceStatus.Ready &&
          item.evaluationPodExpirationDate === null
        ) {
          item.activeDeploymentStatus = ResourceStatus.ScaledDown;
        }

        return item;
      });

      const clientFiltered = statusFilter
        ? patchedData.filter(
            (item) => item.activeDeploymentStatus === statusFilter,
          )
        : patchedData;

      const finalData = sortByStatus(
        clientFiltered,
        sorting?.direction ?? 'asc',
      );

      return {
        data: finalData,
        totalCount: nonFilteredTotal,
        filteredCount: finalData.length,
        hasMoreData: false,
        pagingInformation: undefined,
      };
    },
  };

  return (
    <NavigationExplorer<CustomizableService>
      title="Customizable Services"
      keyProperty="id"
      stationKey="CustomizableServicesExplorer"
      columns={explorerColumns}
      dataProvider={dataProvider}
      calculateNavigateUrl={(item: CustomizableService) =>
        `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/${item.id}`
      }
      onCreateAction={
        config.serviceDeploymentEnabled()
          ? `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/customizable-services/create`
          : ''
      }
      filterOptions={[
        {
          label: 'Name',
          property: 'name',
          type: FilterTypes.FreeText,
        },
        {
          label: 'Service ID',
          property: 'serviceId',
          type: FilterTypes.FreeText,
        },
        {
          label: 'Owned By Axinom',
          property: 'ownedByAxinom',
          type: FilterTypes.Options,
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
        },
        {
          label: 'Status',
          property: 'activeDeploymentStatus',
          type: FilterTypes.Options,
          options: filterOptionsForResourceStatus.map((key) => ({
            value: { ...ResourceStatus, type: 'enum' }[key],
            label: getEnumLabel({ ...ResourceStatus, type: 'enum' }[key] ?? ''),
          })),
        },
      ]}
    />
  );
};

export const CustomizableServicesCrumb: BreadcrumbResolver = () =>
  'Customizable Services';
